import React, { useState, useEffect, useContext } from 'react';
import { getNotificationsByUser, deleteNotification, getNotificationById, markNotificationAsViewed, getAllNotifications, getUserInfo, getUsers } from '../api/api';
import NotificationForm from '../components/NotificationForm';
import { AuthContext } from '../context/AuthContext';
import '../index.css';
import './Users.css'; // Reuse Users CSS
import Table from '../components/common/Table';
import Header from '../components/common/Header';
import Search from '../components/common/Search';
import Button from '../components/common/Button';
import Error from '../components/common/Error';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [notificationToEdit, setNotificationToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState([]);
    const { userRole, token } = useContext(AuthContext);

    const canCreate = userRole === "Director" || userRole === "Navegador";
    const canUpdate = userRole === "Director" || userRole === "Navegador";
    const canDelete = userRole === "Director" || userRole === "Navegador";
    const canRead = userRole === "Director" || userRole === "Navegador" || userRole === "Lector";

    useEffect(() => {
        const fetchNotifications = async () => {
            setLoading(true);
            try {
                let notificationsData;
                if (userRole === "Director") {
                    notificationsData = await getAllNotifications();
                } else {
                    const userInfo = await getUserInfo(token);
                    notificationsData = await getNotificationsByUser(userInfo.email);
                }
                setNotifications(notificationsData);
                const usersData = await getUsers();
                setUsers(usersData);
            } catch (err) {
                setError(err.message || 'Error al cargar las notificaciones');
            } finally {
                setLoading(false);
            }
        };
        if (canRead) {
            fetchNotifications();
        } else {
            setError("No tienes permisos para ver las notificaciones")
        }
    }, [canRead, userRole, token]);

    const handleCreate = () => {
        setNotificationToEdit(null);
        setShowForm(true);
    };

    const handleEdit = async (id) => {
        try {
            const notification = await getNotificationById(id);
            setNotificationToEdit(notification);
            setShowForm(true);
        } catch (error) {
            setError(error.message || 'Error al cargar la notificación');
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteNotification(id);
            setNotifications(notifications.filter(notification => notification.id !== id));
        } catch (err) {
            setError(err.message || 'Error al eliminar la notificación');
        }
    };

    const handleView = async (id) => {
        try {
            await markNotificationAsViewed(id);
            setNotifications(notifications.map(notification => {
                if (notification.id === id) {
                    return { ...notification, viewed: true };
                }
                return notification;
            }));
        } catch (err) {
            setError(err.message || 'Error al marcar la notificación como vista');
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setNotificationToEdit(null);
    }

    const handleNotificationUpdated = (newNotification) => {
        setNotifications(notifications.map((notification) => (notification.id === newNotification.id ? newNotification : notification)))
    }

    const handleNotificationCreated = (newNotification) => {
        setNotifications([...notifications, newNotification])
    }

    const filteredNotifications = notifications.filter((notification) => {
        const notificationValues = Object.values(notification).join('').toLowerCase();
        return notificationValues.includes(searchTerm.toLowerCase());
    });

    if (loading) return <div>Cargando...</div>;
    if (error) return <Error error={error} />;

    const tableHeaders = ["Título", "Fecha de Envío", "Detalles", "Tipo", "Usuario", "Vista", "Acciones"];

    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + "...";
        }
        return text;
    };

    const renderNotificationRow = (notification) => [
        truncateText(notification.title, 30),
        notification.send_date,
        truncateText(notification.details, 30),
        notification.type,
        notification.user_email,
        notification.viewed ? "Sí" : "No",
        [
            canUpdate && <Button onClick={() => handleEdit(notification.id)}>Editar</Button>,
            canDelete && <Button onClick={() => handleDelete(notification.id)}>Eliminar</Button>,
            <Button onClick={() => handleView(notification.id)}>Marcar como vista</Button>
        ]
    ];

    return (
        <div className='container'>
            <Header title="Gestión de Notificaciones" />
            <Search searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            {canCreate && <Button onClick={handleCreate}>Crear Notificación</Button>}
            {showForm && <div className='container-form'><NotificationForm onClose={handleCloseForm} notification={notificationToEdit} onNotificationUpdated={handleNotificationUpdated} onNotificationCreated={handleNotificationCreated} users={users} /></div>}
            <Table headers={tableHeaders} data={filteredNotifications} renderRow={renderNotificationRow} />
        </div>
    );
};

export default Notifications;
